import type { BookingFormAuditStats } from "./BookingFormAuditAnalytics";
import {
  getSessionCustomerProgress,
  formatDevice,
  formatEngagement,
  formatLandingPage,
  formatLocation,
  formatTrafficSource,
  getCustomerDisplayName,
  getFormattedProgressEntries,
  getRouteSummary,
  type AuditSession,
  type SessionClientFilters,
} from "../../utils/bookingFormAuditSession";
import { formatAmount } from "../../utils/formatMoney";
import { formatUkDate, formatUkDateTime } from "../../utils/dateUtils";
import { auditStepLabel } from "../../utils/bookingFormAuditSteps";

const BOOKING_SOURCE_LABELS: Record<string, string> = {
  "southampton-port-taxi": "Southampton Port Taxi",
  "airport-transfers-taxi": "Airport Transfers Taxi",
  "uk-airport-taxi": "UK Airport Taxi",
  "liverpool-dock-taxi": "Liverpool Docks Taxi",
  "tikla-cars": "Tikla Cars",
};

function formatSource(source: string): string {
  return BOOKING_SOURCE_LABELS[source] ?? source;
}

function formatDateTime(value: string): string {
  try {
    return formatUkDateTime(value);
  } catch {
    return value;
  }
}

function formatDate(value: string): string {
  try {
    return formatUkDate(value);
  } catch {
    return value;
  }
}

export function buildApiFilterSummary(filters: {
  bookingSource: string;
  fromDate: string;
  toDate: string;
  customerEmail: string;
  sessionId: string;
}): string[] {
  const lines: string[] = [];
  if (filters.bookingSource) {
    lines.push(`Website: ${formatSource(filters.bookingSource)}`);
  }
  if (filters.fromDate) lines.push(`From: ${formatDate(filters.fromDate)}`);
  if (filters.toDate) lines.push(`To: ${formatDate(filters.toDate)}`);
  if (filters.customerEmail) lines.push(`Email: ${filters.customerEmail}`);
  if (filters.sessionId) lines.push(`Session ID: ${filters.sessionId}`);
  return lines;
}

export function buildClientFilterSummary(filters: SessionClientFilters): string[] {
  const lines: string[] = [];
  if (filters.audience === "leads") {
    lines.push("Audience: leads / real customers");
  } else if (filters.audience === "bots") {
    lines.push("Audience: bots / scrapers");
  } else {
    lines.push("Audience: all traffic");
  }
  if (filters.status !== "all") {
    lines.push(`Status: ${filters.status}`);
  }
  if (filters.lastStep) {
    lines.push(`Last step: ${auditStepLabel(filters.lastStep)}`);
  }
  if (filters.contact !== "all") {
    lines.push(`Contact: ${filters.contact.replace(/_/g, " ")}`);
  }
  if (filters.route !== "all") {
    lines.push(`Route: ${filters.route.replace(/_/g, " ")}`);
  }
  if (filters.search) lines.push(`Search: ${filters.search}`);
  if (filters.landingPage) lines.push(`Landing page: ${filters.landingPage}`);
  if (filters.trafficSource) lines.push(`Traffic: ${filters.trafficSource.replace(/_/g, " ")}`);
  if (filters.hadValidationError) lines.push("Had validation errors");
  if (filters.hadFareQuote) lines.push("Fare quote attempted");
  if (filters.hadFareQuoteFailure) lines.push("Fare quote failed");
  if (filters.hadPaymentFailure) lines.push("Payment failed");
  if (filters.hadBookingFailure) lines.push("Booking save failed");
  if (filters.cardEntry === "completed") lines.push("Card details entered");
  if (filters.cardEntry === "not_completed") lines.push("Reached payment, no card");
  if (filters.cardEntry === "error") lines.push("Card input error");
  if (filters.reachedPayment) lines.push("Reached payment");
  if (filters.cookieConsent) lines.push("Cookie consent given");
  if (filters.reachedStep) {
    lines.push(`Reached step: ${auditStepLabel(filters.reachedStep)}`);
  }
  if (filters.deviceType) lines.push(`Device: ${filters.deviceType}`);
  if (filters.country) lines.push(`Country: ${filters.country}`);
  if (filters.browser) lines.push(`Browser: ${filters.browser}`);
  return lines;
}

export function triggerAuditReportPrint(): void {
  document.body.setAttribute("data-print", "audit-report");
  const cleanup = () => {
    document.body.removeAttribute("data-print");
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);
  window.print();
}

interface Props {
  stats: BookingFormAuditStats | null;
  sessions: AuditSession[];
  apiFilters: {
    bookingSource: string;
    fromDate: string;
    toDate: string;
    customerEmail: string;
    sessionId: string;
  };
  clientFilters: SessionClientFilters;
  sessionCountTotal: number;
  vehicleNames?: Record<string, string>;
}

function SummaryTable({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; value: string | number }[];
}) {
  return (
    <section className="audit-print-section">
      <h2 className="audit-print-h2">{title}</h2>
      <table className="audit-print-table">
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th>{row.label}</th>
              <td>{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export default function BookingFormAuditPrintReport({
  stats,
  sessions,
  apiFilters,
  clientFilters,
  sessionCountTotal,
  vehicleNames,
}: Props) {
  const generatedAt = formatUkDateTime(new Date());
  const apiSummary = buildApiFilterSummary(apiFilters);
  const clientSummary = buildClientFilterSummary(clientFilters);

  const leadsWithData = sessions.filter((session) => {
    const progress = getSessionCustomerProgress(session);
    return Object.keys(progress).length > 0 || session.customerEmail;
  });

  return (
    <div className="audit-print-report hidden bg-white text-black">
      <header className="audit-print-header">
        <h1 className="audit-print-h1">Website Audit Report</h1>
        <p className="audit-print-meta">Generated: {generatedAt}</p>
        <p className="audit-print-meta">
          Sessions in report: {sessions.length}
          {sessions.length !== sessionCountTotal
            ? ` (filtered from ${sessionCountTotal} loaded)`
            : ""}
        </p>
        {(apiSummary.length > 0 || clientSummary.length > 0) && (
          <div className="audit-print-filters">
            <strong>Filters applied</strong>
            <ul>
              {[...apiSummary, ...clientSummary].map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}
      </header>

      {stats && (
        <>
          <SummaryTable
            title="Overview"
            rows={[
              { label: "Total sessions", value: stats.totalSessions },
              { label: "Completed bookings", value: stats.completedSessions },
              { label: "Abandoned sessions", value: stats.abandonedSessions },
              { label: "Completion rate", value: `${stats.completionRate}%` },
              {
                label: "Avg session duration",
                value:
                  stats.avgSessionDurationMinutes != null
                    ? `${stats.avgSessionDurationMinutes} min`
                    : "—",
              },
              { label: "Cookie consent rate", value: `${stats.cookieConsentRate}%` },
              { label: "Validation failures", value: stats.validationFailures },
              { label: "Fare quote failures", value: stats.fareQuoteFailures },
              { label: "Payment failures", value: stats.paymentFailures },
              {
                label: "Avg time on site",
                value:
                  stats.avgEngagementSeconds != null
                    ? `${stats.avgEngagementSeconds}s`
                    : "—",
              },
              {
                label: "Avg scroll depth",
                value:
                  stats.avgScrollDepth != null ? `${stats.avgScrollDepth}%` : "—",
              },
            ]}
          />

          {stats.trafficSources.length > 0 && (
            <section className="audit-print-section">
              <h2 className="audit-print-h2">Traffic sources</h2>
              <table className="audit-print-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.trafficSources.map((row) => (
                    <tr key={row.source}>
                      <td>{row.label}</td>
                      <td>{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {stats.topLandingPages.length > 0 && (
            <section className="audit-print-section">
              <h2 className="audit-print-h2">Top landing pages</h2>
              <table className="audit-print-table">
                <thead>
                  <tr>
                    <th>Path</th>
                    <th>Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.topLandingPages.map((row) => (
                    <tr key={row.path}>
                      <td>{row.path}</td>
                      <td>{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          <section className="audit-print-section">
            <h2 className="audit-print-h2">Step funnel</h2>
            <table className="audit-print-table">
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Sessions</th>
                </tr>
              </thead>
              <tbody>
                {stats.stepFunnel.map((row) => (
                  <tr key={row.stepName}>
                    <td>{row.label}</td>
                    <td>{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="audit-print-section">
            <h2 className="audit-print-h2">Abandonment by last step</h2>
            <table className="audit-print-table">
              <thead>
                <tr>
                  <th>Last step</th>
                  <th>Abandoned sessions</th>
                </tr>
              </thead>
              <tbody>
                {stats.stepDropOff.map((row) => (
                  <tr key={row.stepName}>
                    <td>{row.label}</td>
                    <td>{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {(stats.deviceBreakdown.length > 0 || stats.topCountries.length > 0) && (
            <section className="audit-print-section audit-print-columns">
              {stats.deviceBreakdown.length > 0 && (
                <div>
                  <h2 className="audit-print-h2">Devices</h2>
                  <table className="audit-print-table">
                    <thead>
                      <tr>
                        <th>Device</th>
                        <th>Sessions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.deviceBreakdown.map((row) => (
                        <tr key={row.deviceType}>
                          <td>{row.deviceType}</td>
                          <td>{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {stats.topCountries.length > 0 && (
                <div>
                  <h2 className="audit-print-h2">Top countries</h2>
                  <table className="audit-print-table">
                    <thead>
                      <tr>
                        <th>Country</th>
                        <th>Sessions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.topCountries.map((row) => (
                        <tr key={row.country}>
                          <td>{row.country}</td>
                          <td>{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      )}

      <section className="audit-print-section audit-print-page-break">
        <h2 className="audit-print-h2">Potential customers summary</h2>
        <table className="audit-print-table audit-print-table-compact">
          <thead>
            <tr>
              <th>#</th>
              <th>Website</th>
              <th>Started</th>
              <th>Status</th>
              <th>Last step</th>
              <th>Route</th>
              <th>Fare</th>
              <th>Customer</th>
              <th>Contact</th>
              <th>Location</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session, index) => {
              const progress = getSessionCustomerProgress(session);
              const email =
                (session.customerEmail ?? String(progress.email ?? "")) || "—";
              const phone = String(progress.mobile ?? "") || "—";
              const fareValue = formatAmount(progress.fare, 0);
              const fare = fareValue === "N/A" ? "—" : fareValue;

              return (
                <tr key={session.sessionId}>
                  <td>{index + 1}</td>
                  <td>{formatSource(session.bookingSource)}</td>
                  <td>{formatDateTime(session.startedAt)}</td>
                  <td>{session.completed ? "Completed" : "Abandoned"}</td>
                  <td>
                    {session.lastStepName
                      ? auditStepLabel(session.lastStepName)
                      : "—"}
                  </td>
                  <td>{getRouteSummary(progress)}</td>
                  <td>{fare}</td>
                  <td>{getCustomerDisplayName(progress)}</td>
                  <td>
                    {email}
                    {phone !== "—" ? ` · ${phone}` : ""}
                  </td>
                  <td>{formatLocation(session.visitor)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {leadsWithData.length > 0 && (
        <section className="audit-print-section audit-print-page-break">
          <h2 className="audit-print-h2">Customer form details</h2>
          <p className="audit-print-meta">
            Fields captured from booking form snapshots and field updates.
          </p>
          {leadsWithData.map((session, index) => {
            const progress = getSessionCustomerProgress(session);
            const entries = getFormattedProgressEntries(progress, {
              events: session.events,
              vehicleNames,
            });
            if (entries.length === 0 && !session.customerEmail) return null;

            return (
              <article key={session.sessionId} className="audit-print-lead">
                <h3 className="audit-print-h3">
                  Lead {index + 1} — {getCustomerDisplayName(progress)} (
                  {session.completed ? "Completed" : "Abandoned"})
                </h3>
                <p className="audit-print-meta">
                  {formatSource(session.bookingSource)} · Started{" "}
                  {formatDateTime(session.startedAt)} · Session {session.sessionId}
                </p>
                <p className="audit-print-meta">
                  Landing: {formatLandingPage(session.visitor)} · Device:{" "}
                  {formatDevice(session.visitor)}
                </p>
                <table className="audit-print-table">
                  <tbody>
                    {entries.map((entry) => (
                      <tr key={entry.key}>
                        <th>{entry.label}</th>
                        <td>{entry.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </article>
            );
          })}
        </section>
      )}

      <footer className="audit-print-footer">
        <p>Website Audit — confidential internal report</p>
      </footer>
    </div>
  );
}
